<script setup lang="ts">
    import { vIntersectionObserver } from '@vueuse/components';
    import type { ButtonStyling } from '~/components/atm/Button.vue';

    export interface GoogleFormProps {
        formData: {
            id: string;
            url: string;
            title: string;
            description: string;
            documentTitle: string;
            submitUrl: string;
            sections: Server.ISection[];
        };
        successMessage: string;
        widgetBackground: string;
        selectedProductId?: string | undefined;
        selectedProductName?: string | undefined;
        buttonStyling?: ButtonStyling;
        refresh: (opts?: any) => Promise<void>;
    }
    const props = withDefaults(defineProps<GoogleFormProps>(), {
        selectedProductId: undefined,
        selectedProductName: undefined,
        buttonStyling: 'solid',
    });

    const emit = defineEmits<{
        (e: 'submit'): void;
    }>();

    const { t, te, locale, language, isUS } = useLocales();
    const route = useRoute();
    const nuxtApp = useNuxtApp();

    // Refs
    const success = ref(false);
    const loading = ref(false);
    const hasInteracted = ref(false);
    const hasViewed = ref(false);
    const stepIndex = ref(0);
    const previousSections: (string | null)[] = [];
    const currentSectionId = ref<null | string>(null);
    const nextSectionId = ref<null | string>(null);
    const form = ref<null | HTMLFormElement>(null);
    const formScrollTop = ref();
    const formSubmitError = ref<string>('');
    const phoneError = ref('');

    const { formValues, formErrors, checkFormValid, handleBlur } = useForm(form);

    // computed
    // TODO: check if we can replace with formValid and add handleInput from useForm()
    const isValid = computed(() => {
        return !unref(currentSection)?.items?.find(
            (item) =>
                item.question?.required &&
                (!unref(formValues)[item.question.name] || unref(formValues)[item.question.name] === 'no' || phoneError.value !== ''),
        );
    });
    const currentSection = computed(() => props.formData.sections?.find((s: any) => s.id === unref(currentSectionId)));
    const handlePhoneError = (err: string) => {
        phoneError.value = err;
    };
    // functions
    const updateDropdown = (value: string, options: any) => {
        const selectedOption = options.find((o: any) => o.value === value);
        if (selectedOption?.goToSectionId) nextSectionId.value = selectedOption.goToSectionId;
    };

    const getFormTextType = (validation: string | undefined) => (validation === 'email' ? 'email' : 'text');

    const submitForm = async () => {
        loading.value = true;
        formSubmitError.value = '';
        if (checkFormValid()) {
            // build form
            const body = new FormData();
            body.append('formURL', props.formData.url);
            body.append('formId', props.formData.id);
            let userEmail: string | undefined;

            const emarsysMap: Record<string, string> = {};
            const emarsysData: Record<string, string> = {
                /* eslint-disable camelcase */
                form_title: props.formData.documentTitle,
                url_evt_1: route.fullPath,
                /* eslint-enable camelcase */
            };

            const localeFormatted = locale.value.replace('_', '-');

            if (isUS.value) {
                emarsysData['ietf_inp_2738'] = localeFormatted;
            } else {
                emarsysData['ietf_inp_729'] = localeFormatted;
            }

            const sections = props.formData.sections
                .filter((section) => section.id === unref(currentSectionId) || unref(previousSections).includes(section.id))
                .map((section) => ({
                    idx: section.idx,
                    items: section.items
                        .map((item) => {
                            if (!item.question) return null;
                            const name = item.question.name;
                            emarsysMap[name] = item.originalTitle;
                            // check if this is the email
                            if (!userEmail && item.originalTitle && item.originalTitle.includes('id:email_inp_3')) {
                                userEmail = unref(formValues)[name];
                            }
                            // Handle wish list product
                            if (item.originalTitle.includes('id:product') && props.selectedProductId) {
                                emarsysData[item.originalTitle] = props.selectedProductId;
                                formValues.value[name] = props.selectedProductId;
                            }
                            if (item.question.type === 'radio') {
                                const hasOtherOption = item.question.options.some((option: { [key: string]: string }) => option.isOther);
                                return hasOtherOption ? [name, `${name}${GOOGLE_FORM_OTHER_POSTFIX}`] : name;
                            }
                            if (item.question.type === 'date')
                                return [
                                    `${name}${GOOGLE_FORM_YEAR_POSTFIX}`,
                                    `${name}${GOOGLE_FORM_MONTH_POSTFIX}`,
                                    `${name}${GOOGLE_FORM_DAY_POSTFIX}`,
                                ];
                            if (item.question.type === 'upload') {
                                const fids = `fids[${name}]`;
                                body.append(fids, item.question.settings.folderId);
                                return [name, fids];
                            }
                            return name;
                        })
                        .filter(Boolean)
                        .flat(),
                }));
            body.append('sections', JSON.stringify(sections));

            for (const key in unref(formValues)) {
                const value = unref(formValues)[key];

                // handle files value
                if (Array.isArray(value)) {
                    for (const file of value as File[]) {
                        body.append(key, file);
                    }
                    continue;
                }

                const emarsysKey = emarsysMap[key];
                if (emarsysKey) emarsysData[emarsysKey] = getOtherValue(isDateValue(value) ? getDateValue(value) : value).value;

                // handle date value
                if (isDateValue(value)) {
                    const dateValues = getDateValue(value).split('-');
                    body.append(`${key}${GOOGLE_FORM_YEAR_POSTFIX}`, dateValues[0]);
                    body.append(`${key}${GOOGLE_FORM_MONTH_POSTFIX}`, dateValues[1]);
                    body.append(`${key}${GOOGLE_FORM_DAY_POSTFIX}`, dateValues[2]);
                    continue;
                }
                // handle other value
                if (isOtherValue(value)) {
                    const otherValue = getOtherValue(value);

                    body.append(`${key}_sentinel`, '');
                    if (otherValue.isOther) {
                        body.append(key, '');
                        body.append(`${key}${GOOGLE_FORM_OTHER_POSTFIX}`, otherValue.value);
                    } else {
                        body.append(key, otherValue.value);
                    }
                    continue;
                }
                // handle normal value
                body.append(key, value);
            }
            body.append('emarsys', JSON.stringify(emarsysData));

            // submit form

            const response = await fetch(props.formData.submitUrl, {
                body,
                method: 'POST',
                mode: 'cors',
            }).catch((error: Error) => {
                return new Response(error.message, {
                    status: (error as any).statusCode || 400,
                });
            });

            const json = await response.json();

            if (!response.ok || (json as any).error) {
                console.error(json);
                if (response.status === 409) {
                    // reload form with no cache
                    await props.refresh();
                    formSubmitError.value = t('forms.formChanged');
                    currentSectionId.value = null;
                } else {
                    formSubmitError.value = t('forms.submitError');
                }
                loading.value = false;
                return;
            }

            //adding product data in case of out of stock form
            const productData = props.selectedProductId ? { item_id: props.selectedProductId, item_name: props.selectedProductName } : null;
            nuxtApp.$gtm.ecommerceFormInteractions(
                'form_submit',
                generateFormData({
                    /* eslint-disable camelcase */
                    form_destination: props.formData.submitUrl,
                    ...productData,
                    /* eslint-enable camelcase */
                }),
            );
            emit('submit');

            if (userEmail) {
                nuxtApp.$gtm.pushEvent({
                    event: 'form_submit',
                    emarsys: {
                        email: userEmail,
                        category: '',
                    },
                });
            }

            loading.value = false;
            success.value = true;
            scrollTop();
        }
        formSubmitError.value = t('forms.invalidValues');
        loading.value = false;
    };

    const scrollTop = () => {
        if (unref(formScrollTop)) {
            unref(formScrollTop).scrollIntoView({ behavior: 'smooth' });
        }
    };

    const handleFirstInteraction = () => {
        if (hasInteracted.value) return;
        hasInteracted.value = true;
        nuxtApp.$gtm.ecommerceFormInteractions('form_start', generateFormData({ step: stepIndex.value }));
    };

    const clickNextSection = (_ev: MouseEvent) => {
        if (checkFormValid()) {
            stepIndex.value += 1;
            previousSections.push(unref(currentSectionId));
            // goto next section
            // - either selected by dropdown
            // - or first go to section
            currentSectionId.value = unref(nextSectionId) || currentSection.value?.hasGoTos?.[0] || null;
            scrollTop();

            nuxtApp.$gtm.ecommerceFormInteractions('form_progress', generateFormData({ step: stepIndex.value }));
        }
    };

    const clickPreviousSection = (_ev: MouseEvent) => {
        nextSectionId.value = unref(currentSectionId);
        scrollTop();
        // @ts-ignore
        if (previousSections.length) currentSectionId.value = previousSections.pop();
        stepIndex.value -= 1;
    };

    const translateFuntion = (label: string) => {
        if (!label) return label;
        const tranlationKey = `forms.${label}`;
        return te(tranlationKey, language.value) ? t(tranlationKey) : label;
    };

    const generateFormData = (additionalData: Record<string, string | number> = {}) => ({
        /* eslint-disable camelcase */
        form_id: props.formData.id,
        lead_type: props.formData.documentTitle,
        /* eslint-enable camelcase */
        ...additionalData,
    });

    function onIntersectionObserver([{ isIntersecting }]: IntersectionObserverEntry[]) {
        if (hasViewed.value || !isIntersecting) return;
        nuxtApp.$gtm.ecommerceFormInteractions('form_view', generateFormData());
        hasViewed.value = true;
    }
</script>
<template>
    <section
        v-if="!success"
        ref="formScrollTop"
        v-intersection-observer="onIntersectionObserver"
        class="org-google-form scroll-mt-16 md:scroll-mt-32">
        <form
            v-if="formData"
            :id="formData.id"
            ref="form"
            enctype="multipart/form-data"
            class="floating-labels animate-floating-labels display-table"
            @focus.capture="handleFirstInteraction"
            @submit.prevent="submitForm">
            <div class="mb-6 flex flex-col gap-2">
                <lazy-atm-heading
                    v-if="currentSection?.title"
                    level="h3">
                    <renderer-html :content="woomTextFormat(currentSection.title)" />
                </lazy-atm-heading>

                <p
                    v-if="currentSection?.description"
                    class="link-style">
                    <renderer-html :content="woomTextFormat(currentSection.description)" />
                </p>
            </div>
            <client-only>
                <template
                    v-for="item in currentSection?.items ?? []"
                    :key="`${formData.id}-${item.id}`">
                    <template v-if="item.type === 'question' && item.question">
                        <lazy-mol-form-phone-number
                            v-if="item.question.type === 'text' && item.question.validation === 'phone'"
                            v-model:text-value="formValues[item.question.name]"
                            :for-id="item.question.name"
                            :required="item.question.required"
                            :hidden="item.question.hidden"
                            :label="item.title"
                            :label-background="widgetBackground"
                            :error="formErrors[item.question.name]"
                            @blur="handleBlur"
                            @update:error="handlePhoneError" />
                        <lazy-mol-form-text
                            v-else-if="item.question.type === 'text'"
                            v-model:text-value="formValues[item.question.name]"
                            :for-id="item.question.name"
                            :required="item.question.required"
                            :hidden="item.question.hidden"
                            :label="item.title"
                            :label-background="widgetBackground"
                            :error="formErrors[item.question.name]"
                            :type="getFormTextType(item.question.validation)"
                            @blur="handleBlur" />
                        <lazy-mol-form-checkbox
                            v-else-if="item.question.type === 'checkbox'"
                            v-model:checked-value="formValues[item.question.name]"
                            :for-id="item.question.name"
                            :true-value="item.question.options[0].value"
                            false-value=""
                            :required="item.question.required"
                            :hidden="item.question.hidden"
                            :title="item.title"
                            :description="item.question.description"
                            :label="item.question.options[0].value"
                            :error="formErrors[item.question.name]" />
                        <lazy-mol-form-date
                            v-else-if="item.question.type === 'date'"
                            v-model:date-value="formValues[item.question.name]"
                            :for-id="item.question.name"
                            :required="item.question.required"
                            :hidden="item.question.hidden"
                            :label="item.title"
                            :label-background="widgetBackground"
                            :error="formErrors[item.question.name]"
                            @blur="handleBlur" />
                        <lazy-mol-form-radio-group
                            v-else-if="item.question.type === 'radio'"
                            v-model:checked-value="formValues[item.question.name]"
                            :for-id="item.question.name"
                            :required="item.question.required"
                            :hidden="item.question.hidden"
                            :title="item.title"
                            :options="item.question.options"
                            :error="formErrors[item.question.name]" />
                        <lazy-mol-form-select
                            v-else-if="item.question.type === 'drop_down'"
                            v-model:dropdown-value="formValues[item.question.name]"
                            :options="item.question.options"
                            :required="item.question.required"
                            :hidden="item.question.hidden"
                            :label="item.title"
                            :label-background="widgetBackground"
                            :translate="item.question.translate ? translateFuntion : undefined"
                            @update:dropdown-value="(ev: any) => updateDropdown(ev, item.question?.options)" />
                        <lazy-mol-form-file
                            v-else-if="item.question.type === 'upload'"
                            v-model:files="formValues[item.question.name]"
                            :for-id="item.question.name"
                            :required="item.question.required"
                            :hidden="item.question.hidden"
                            :label="item.title"
                            :multiple="item.question.settings.multi === 'true'"
                            :number-of-files="item.question.settings.numFiles"
                            :error="formErrors[item.question.name]" />
                        <lazy-mol-form-textarea
                            v-else-if="item.question.type === 'textarea'"
                            v-model:textarea-value="formValues[item.question.name]"
                            :for-id="item.question.name"
                            :required="item.question.required"
                            :hidden="item.question.hidden"
                            :label="item.title"
                            :label-background="widgetBackground"
                            :error="formErrors[item.question.name]" />
                    </template>
                    <template v-else-if="item.type == 'image'">
                        <img
                            :src="item.image?.contentUri"
                            class="mb-5 h-auto w-full rounded"
                            loading="lazy"
                            role="presentation" />
                    </template>
                    <template v-else-if="item.type == 'text'">
                        <div class="my-6 flex flex-col gap-2">
                            <lazy-atm-heading
                                v-if="item.title"
                                level="h4">
                                <renderer-html :content="woomTextFormat(item.title)" />
                            </lazy-atm-heading>

                            <p
                                v-if="item.description"
                                class="link-style">
                                <renderer-html :content="woomTextFormat(item.description)" />
                            </p>
                        </div>
                    </template>
                    <template v-else> not a question: {{ item }} </template>
                </template>
            </client-only>

            <div class="mt-4 flex gap-3">
                <template v-if="currentSection?.hasGoTos.length === 0 && !currentSection?.hasPrevious">
                    <atm-button
                        :loading="loading"
                        :styling="buttonStyling">
                        {{ $t('forms.submit') }}
                    </atm-button>
                </template>

                <template v-else>
                    <lazy-atm-button
                        v-if="currentSection?.hasPrevious"
                        type="button"
                        styling="solid-secondary"
                        @click="clickPreviousSection">
                        {{ $t('back') }}
                    </lazy-atm-button>

                    <lazy-atm-button
                        v-if="!currentSection?.hasSubmit"
                        :disabled="!isValid"
                        type="button"
                        styling="solid-secondary"
                        @click="clickNextSection">
                        {{ $t('next') }}
                    </lazy-atm-button>

                    <lazy-atm-button
                        v-else
                        :styling="buttonStyling"
                        :loading="loading"
                        :disabled="!isValid">
                        {{ $t('forms.submit') }}
                    </lazy-atm-button>
                </template>
            </div>
            <p
                v-if="formSubmitError"
                class="mt-3 whitespace-pre-line font-mono text-sm text-woom-red"
                role="alert">
                {{ formSubmitError }}
            </p>
        </form>
    </section>
    <div
        v-else
        class="success-message link-style inline">
        <renderer-html :content="woomTextFormat(successMessage)" />
    </div>
</template>
